import { useMemo } from 'react';
import {
  fmt,
  fmtMoney,
  tsaTimeline,
  type SeparationState,
  type TsaTimelineRow,
} from '@integrate/core';

/**
 * The exit timeline, ported from `tsaTimelineHtml` (orgchart.html:6566–6600).
 *
 * One row per agreement, months after Day 1 across the track: how long each service runs, and when its
 * replacement is due. The row model — every month and every flag — comes from `tsaTimeline` in core, so
 * the parity harness can hold it; this file places marks and says what they mean.
 *
 * Three things it does that the original did not, each closing a finding rather than a preference:
 *
 * · An agreement with no agreed term gets an open-ended hatched bar instead of an empty track. The
 *   original gated the bar on `dur > 0`, so €300,000 a year of commitment rendered as a label, grid
 *   lines and no mark of any kind.
 * · A replacement with no needed-by month is named in the row rather than placed at Day 1. On a month
 *   axis there is nowhere honest to draw it, and month zero is a claim, not a blank.
 * · Every failure state carries a glyph and words as well as a hue. A red bar meant "this service has no
 *   replacement and will run uncovered" and a colour-blind reader saw a bar; the explanation lived in a
 *   `title` attribute, which never fires for a keyboard user. The table twin below carries all of it as
 *   text, which is also what makes the chart assertable without measuring pixels.
 */
export function TsaTimeline({
  state,
  onAddReplacement,
}: {
  state: SeparationState;
  /**
   * Create the replacement this row says is missing, with the agreement already linked.
   *
   * Absent for a reader, which is why it is optional rather than a disabled button: a control that can
   * only be refused is the defect P4l-g spent a commit on.
   */
  onAddReplacement?: (tsaId: string) => void;
}): JSX.Element | null {
  const T = useMemo(() => tsaTimeline(state), [state]);
  if (!T.rows.length) return null;

  const pct = (m: number): string => `${Math.min(100, (m / T.horizon) * 100)}%`;
  const ticks: number[] = [0];
  for (let m = T.scaleStep; m <= T.horizon; m += T.scaleStep) ticks.push(m);

  const uncoveredN = T.rows.filter((r) => r.uncovered).length;

  return (
    <section data-testid="tsa-timeline">
      <h3>Exit timeline</h3>
      <p className="muted">
        Each bar is one agreement running from Day 1; the ◆ is when its replacement is needed. The
        receiving side builds the replacement, so a ⇢ received TSA is replaced by a Carve-out Day 2 hire
        and a ⇠ provided one by a Remain hire — a link on the wrong side matches nothing and the row will
        say so.
      </p>

      <div
        className="tl"
        role="img"
        data-horizon={T.horizon}
        data-uncovered={uncoveredN}
        aria-label={
          `Exit timeline over ${T.horizon} months after Day 1. ${fmt(T.rows.length)} ` +
          `${T.rows.length === 1 ? 'agreement' : 'agreements'}, of which ${fmt(uncoveredN)} ` +
          `${uncoveredN === 1 ? 'has' : 'have'} no filled replacement dated before the service ends.`
        }
      >
        {T.rows.map((r) => (
          <TimelineRow key={r.tsa.id} row={r} pct={pct} onAddReplacement={onAddReplacement} />
        ))}

        {/* Placed in the track column explicitly, so the ticks line up whatever width the labels take. */}
        <span />
        <div className="tl-axis" data-testid="tl-axis">
          {ticks.map((m) => (
            <span key={m} style={{ left: pct(m) }}>
              M{m}
            </span>
          ))}
        </div>
      </div>

      <details className="mchart-table">
        <summary>Show the numbers</summary>
        <div className="table-scroll">
          <table className="tsa-table">
            <thead>
              <tr>
                <th>TSA</th>
                <th>Direction</th>
                <th className="num">Ends</th>
                <th>Status</th>
                <th className="num">Replacements due</th>
                <th>Exit</th>
              </tr>
            </thead>
            <tbody>
              {T.rows.map((r) => (
                <tr key={r.tsa.id} data-testid={`tl-data-${r.tsa.id}`}>
                  <td>{r.tsa.name}</td>
                  <td>{r.tsa.direction === 'provided' ? 'Provided (reverse)' : 'Received'}</td>
                  <td className="num">{r.termAgreed ? `M${r.dur}` : 'no agreed term'}</td>
                  <td>{r.tsa.status}</td>
                  <td className="num">
                    {r.markers.length === 0
                      ? 'none linked'
                      : r.markers
                          .map((m) =>
                            [
                              m.month == null ? 'no date' : `M${m.month}`,
                              m.late ? 'late' : null,
                              m.filled ? null : 'unfilled',
                            ]
                              .filter(Boolean)
                              .join(', '),
                          )
                          .join(' · ')}
                  </td>
                  <td>
                    {r.uncovered
                      ? r.wrongSideN > 0
                        ? `not covered — ${fmt(r.wrongSideN)} linked on the wrong side`
                        : 'not covered'
                      : r.tsa.status === 'exited'
                        ? 'exited'
                        : 'covered'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </section>
  );
}

function TimelineRow({
  row,
  pct,
  onAddReplacement,
}: {
  row: TsaTimelineRow;
  pct(m: number): string;
  onAddReplacement?: (tsaId: string) => void;
}): JSX.Element {
  const { tsa } = row;
  const arrow = tsa.direction === 'provided' ? '⇠' : '⇢';
  const barClass = [
    'tl-bar',
    tsa.direction === 'provided' ? 'provided' : '',
    row.uncovered ? 'uncovered' : '',
    tsa.status === 'exited' ? 'exited' : '',
    row.termAgreed ? '' : 'no-term',
  ]
    .filter(Boolean)
    .join(' ');

  const lateN = row.markers.filter((m) => m.late).length;
  const unfilledN = row.markers.filter((m) => !m.filled).length;

  return (
    <>
      <div className="tl-label" title={`${tsa.name}${tsa.service ? ` — ${tsa.service}` : ''}`}>
        {arrow} {tsa.name}
      </div>
      <div
        className="tl-track"
        data-testid={`tl-row-${tsa.id}`}
        data-months={row.dur}
        data-uncovered={row.uncovered ? '1' : '0'}
        data-term-agreed={row.termAgreed ? '1' : '0'}
        data-undated={row.undatedN}
        data-late={lateN}
        data-unfilled={unfilledN}
        data-billed-to={row.billedTo ?? ''}
      >
        <div
          className={barClass}
          style={row.termAgreed ? { width: pct(row.dur) } : undefined}
          title={
            row.termAgreed
              ? `${tsa.name}: ${fmtMoney(tsa.monthlyCost)}/month for ${row.dur} months` +
                (row.billedTo == null ? '' : `, exited at M${row.billedTo}`)
              : `${tsa.name}: ${fmtMoney(tsa.monthlyCost)}/month, no agreed term`
          }
        />
        {/*
          What it actually billed, drawn over the term it was signed for.

          Without this the bar states twelve months next to a fee tile that bills eight, on the same axis
          and two sections apart, with the exit month readable only in the table above. Drawn only when a
          month was recorded: an exit with no month bills nothing and keeps the greyed full-term bar it
          has always had, which is also the only thing a file from the frozen app can say.
        */}
        {row.termAgreed && row.billedTo != null && (
          <>
            <div
              className="tl-billed"
              style={{ width: pct(row.billedTo) }}
              data-testid={`tl-billed-${tsa.id}`}
              title={`Billed for ${row.billedTo} of ${row.dur} months — exited at M${row.billedTo}`}
            />
            <span className="tl-exit" style={{ left: pct(row.billedTo) }} aria-hidden="true" />
          </>
        )}
        {row.markers
          .filter((m) => m.month != null)
          .map((m) => (
            <span
              key={m.buildUpId}
              className={`tl-marker${m.late ? ' late' : ''}`}
              style={{ left: pct(m.month!) }}
              data-testid={`tl-marker-${m.buildUpId}`}
              data-month={m.month!}
              title={
                `${m.name} — needed by M${m.month}` +
                (m.late ? ', AFTER the agreement ends' : '') +
                (m.filled ? '' : ' (position not filled)')
              }
            />
          ))}
      </div>

      {/*
        The third grid cell, always emitted even when empty: the grid is three columns wide, and a row
        that contributes two cells shifts every row after it.

        This is where the words the colour was carrying on its own go. Each is a REASON, not a badge —
        "uncovered" without saying whether the replacement is missing, unfilled, undated or late is the
        same dead end the tooltip was.
      */}
      {row.uncovered ? (
        <span className="tl-flag" data-testid={`tl-flag-${tsa.id}`} data-wrong-side={row.wrongSideN}>
          ⚠{' '}
          {/*
            The wrong-side case FIRST, because it is the one uncovered flag that is an artefact rather
            than a fact. `tsaReplacements` filters by the side that must build the replacement, so a hire
            linked to this agreement from the other side yields no marker at all — and the row then says
            "no replacement" about somebody who exists, counts in headcount and books hiring cost. The
            only surface that explained it was the diagnostics panel, two clicks away.
          */}
          {row.wrongSideN > 0
            ? `${fmt(row.wrongSideN)} linked ${row.wrongSideN === 1 ? 'hire is' : 'hires are'} on the wrong side`
            : row.markers.length === 0
              ? 'no replacement'
              : unfilledN === row.markers.length
                ? `${fmt(unfilledN)} unfilled`
                : row.undatedN
                  ? `${fmt(row.undatedN)} undated`
                  : 'lands late'}
        </span>
      ) : lateN > 0 ? (
        <span className="tl-flag late" data-testid={`tl-flag-${tsa.id}`}>
          ◆ {fmt(lateN)} late
        </span>
      ) : row.undatedN > 0 ? (
        <span className="tl-flag late" data-testid={`tl-flag-${tsa.id}`}>
          ◆ {fmt(row.undatedN)} undated
        </span>
      ) : (
        <span />
      )}
      {/*
        The action, where the gap is reported. The register's footer offered "Add TSA" alone, so the
        pane whose entire output is rows saying a replacement is missing had no way to create one — the
        app has the button in two places (orgchart.html:6377, :6642) and the platform had it in neither.
        Only on a row that is actually uncovered, and only when the TSA link would help: a wrong-side
        link needs the existing hire fixed, not another one.
      */}
      {onAddReplacement && row.uncovered && row.wrongSideN === 0 && (
        <button
          className="linkish"
          data-testid={`tl-add-replacement-${tsa.id}`}
          onClick={() => onAddReplacement(tsa.id)}
        >
          + replacement
        </button>
      )}
    </>
  );
}
